import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// services
import creditService from "services/credits.js";
import logger from "utils/logger";

const TYPE_LABELS = {
  credit: "Asignación",
  debit: "Consumo",
};

const REF_LABELS = {
  plan_allocation: "Asignación de plan",
  agent_execution: "Ejecución de agente",
  manual_adjustment: "Ajuste manual",
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CardCreditTransactions({ color, refreshTrigger }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter) params.type = typeFilter;
      const response = await creditService.getTransactions(params);
      setTransactions(response.data || []);
      setError(null);
    } catch (err) {
      logger.error("Error loading credit transactions:", err);
      setError("Error al cargar el historial de créditos");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, refreshTrigger]);

  const thClass =
    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
    (color === "light"
      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700");

  return (
    <div
      className={
        "relative flex flex-col min-w-0 break-words w-full shadow-lg rounded " +
        (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
      }
    >
      {/* Header */}
      <div className="rounded-t mb-0 px-4 py-3 border-0">
        <div className="flex flex-wrap items-center">
          <div className="relative w-full px-4 max-w-full flex-grow flex-1">
            <h3
              className={
                "font-semibold text-lg " +
                (color === "light" ? "text-blueGray-700" : "text-white")
              }
            >
              <i className="fas fa-history mr-2 text-blueGray-400"></i>
              Historial de Créditos
            </h3>
          </div>

          {/* Type filter */}
          <div className="relative px-4">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="text-sm px-3 py-2 bg-white border border-blueGray-300 rounded outline-none focus:outline-none focus:ring text-blueGray-600"
            >
              <option value="">Todos los movimientos</option>
              <option value="credit">Solo asignaciones</option>
              <option value="debit">Solo consumos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="block w-full overflow-x-auto">
        <table className="items-center w-full bg-transparent border-collapse">
          <thead>
            <tr>
              <th className={thClass}>Fecha</th>
              <th className={thClass}>Tipo</th>
              <th className={thClass}>Descripción</th>
              <th className={thClass + " text-right"}>Cantidad</th>
              <th className={thClass + " text-right"}>Saldo resultante</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="5" className="text-center py-6 text-blueGray-400">
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Cargando historial...
                </td>
              </tr>
            )}

            {error && (
              <tr>
                <td colSpan="5" className="text-center py-6 text-red-500">
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && transactions.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center py-6 text-blueGray-400">
                  No hay transacciones registradas
                </td>
              </tr>
            )}

            {!loading &&
              !error &&
              transactions.map((tx) => {
                const isCredit = tx.type === "credit";
                return (
                  <tr key={tx.id} className="hover:bg-blueGray-50">
                    {/* Fecha */}
                    <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-blueGray-500">
                      {formatDate(tx.created_at)}
                    </td>

                    {/* Tipo */}
                    <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                      <span
                        className={
                          "text-xs font-medium px-2.5 py-0.5 rounded " +
                          (isCredit
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700")
                        }
                      >
                        <i
                          className={
                            "fas mr-1 " +
                            (isCredit ? "fa-arrow-up" : "fa-arrow-down")
                          }
                        ></i>
                        {TYPE_LABELS[tx.type] || tx.type}
                      </span>
                    </td>

                    {/* Descripción */}
                    <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs p-4 text-blueGray-600 max-w-xs">
                      <div className="font-medium">
                        {tx.description || REF_LABELS[tx.reference_type] || "—"}
                      </div>
                      {tx.reference_type && tx.description && (
                        <div className="text-blueGray-400 mt-0.5">
                          {REF_LABELS[tx.reference_type] || tx.reference_type}
                        </div>
                      )}
                    </td>

                    {/* Cantidad */}
                    <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right">
                      <span
                        className={
                          "font-bold text-sm " +
                          (isCredit ? "text-emerald-600" : "text-red-600")
                        }
                      >
                        {isCredit ? "+" : "-"}
                        {tx.amount}
                      </span>
                    </td>

                    {/* Saldo resultante */}
                    <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right">
                      <span className="font-semibold text-blueGray-700">
                        {tx.balance_after}
                      </span>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

CardCreditTransactions.defaultProps = {
  color: "light",
  refreshTrigger: 0,
};

CardCreditTransactions.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
  refreshTrigger: PropTypes.number,
};
